import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, Progress, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { ChecklistOut } from "../shared/api-types";
import { checklistHref } from "./dashboard-model";
import { useDismissChecklist } from "./use-dashboard";

interface SetupChecklistProps {
  checklist: ChecklistOut;
  agentId: string | null;
}

/** Dismissible "Finish setting up your assistant" card; steps are derived server-side from real state. */
export function SetupChecklist({ checklist, agentId }: SetupChecklistProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const message = useErrorMessage();
  const dismiss = useDismissChecklist();
  const [hiddenLocally, setHiddenLocally] = useState(false);
  if (checklist.dismissed || hiddenLocally || checklist.completed >= checklist.total) return null;
  const canDismissForAll = permissions.can("workspace.manage") && !permissions.readOnly;
  const onDismiss = () => {
    setHiddenLocally(true);
    if (!canDismissForAll) return;
    dismiss.mutate(undefined, {
      onSuccess: () => toast.success(t("dashboard.checklist.dismissed")),
      onError: (error) => {
        setHiddenLocally(false);
        toast.error(message(error));
      },
    });
  };
  const progress = t("dashboard.checklist.progress", { done: checklist.completed, total: checklist.total });
  return (
    <section className="card" aria-labelledby="setup-title" data-testid="setup-checklist">
      <div className="card-body flex flex-col gap-5 lg:flex-row lg:items-center">
        <div className="shrink-0 lg:w-72">
          <Badge tone="warning" dot>
            {progress}
          </Badge>
          <h2 id="setup-title" className="mt-2 text-base font-bold">
            {t("dashboard.checklist.title")}
          </h2>
          <Progress className="mt-3" value={checklist.completed} max={checklist.total} label={t("dashboard.checklist.progressLabel")} valueText={progress} />
        </div>
        <ol className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {checklist.items.map((item) => (
            <li key={item.key}>
              {item.done ? (
                <span className="flex items-center gap-2.5 rounded-md bg-surface-2 px-3 py-2.5 text-sm text-fg-muted line-through decoration-fg-subtle">
                  <Icon name="check-circle" className="size-4 text-success" />
                  <span className="sr-only">{t("dashboard.checklist.done")}: </span>
                  {t(`dashboard.checklist.steps.${item.key}`)}
                </span>
              ) : (
                <AppLink
                  href={checklistHref(item.key, summary.slug, agentId)}
                  className="flex items-center gap-2.5 rounded-md border border-line px-3 py-2.5 text-sm font-semibold hover:border-primary hover:bg-primary-soft"
                >
                  <Icon name="circle" className="size-4 text-fg-subtle" />
                  {t(`dashboard.checklist.steps.${item.key}`)}
                  <Icon name="arrow-right" className="ml-auto size-4 text-fg-subtle" />
                </AppLink>
              )}
            </li>
          ))}
        </ol>
        <Button variant="ghost" size="sm" iconOnly className="self-start" aria-label={t("dashboard.checklist.dismiss")} onClick={onDismiss}>
          <Icon name="x" />
        </Button>
      </div>
    </section>
  );
}
